import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, ChefHat, BookOpen, Users, Award, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useImages } from '../hooks/useImages';

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } }) };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const RiseAndRolls = () => {
  const siteImages = useImages();
  const img = (slug, fb) => siteImages[slug] || fb;

  const courses = [
    { icon: BookOpen, title: 'Kids Culinary', desc: 'Fun cooking classes for children aged 6-14', duration: '3 months' },
    { icon: Users, title: 'Youth Programs', desc: 'Professional culinary training for aspiring chefs', duration: '6 months' },
    { icon: ChefHat, title: 'Masterclasses', desc: 'Advanced techniques from celebrity chefs', duration: 'Weekend workshops' },
    { icon: Award, title: 'Chef Incubation', desc: 'Launch your own food business with expert guidance', duration: '12 months' }
  ];

  const franchise = [
    { icon: ChefHat, title: 'Complete Setup', desc: 'Kitchen equipment, curriculum, and branding' },
    { icon: Users, title: 'Training Support', desc: 'Instructor training and certification' },
    { icon: Award, title: 'Brand Power', desc: 'Marketing and reputation support' }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FDF8F0' }}>
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5C7A3A]/8 to-[#D4A574]/5"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial="hidden" animate="visible" variants={stagger}>
            <div className="space-y-6">
              <motion.span variants={fadeUp} custom={0} className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#C4844A] bg-[#C4844A]/8 px-4 py-2 rounded-full border border-[#C4844A]/15">
                Culinary Education &middot; Cultural Pride &middot; Excellence
              </motion.span>
              <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Rise & <span className="text-[#5C7A3A]">Rolls</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="text-base text-[#5a4535] leading-relaxed max-w-lg">
                Where passion meets profession. Our culinary academy combines traditional Indian cooking wisdom with modern techniques, nurturing the next generation of food entrepreneurs.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Book a Class', '_blank')} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-8 py-5 rounded-lg shadow-lg shadow-[#5C7A3A]/20 transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="rise-book-btn">
                  Book a Class <ArrowRight className="ml-2" size={18} />
                </Button>
                <Button size="lg" variant="outline" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Course Catalog Request', '_blank')} className="border-2 border-[#5C7A3A] text-[#5C7A3A] hover:bg-[#5C7A3A] hover:text-white font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="rise-courses-btn">
                  View Courses
                </Button>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} custom={2} className="grid grid-cols-2 gap-3">
              <img src={img('rise-hero-1', 'https://images.unsplash.com/photo-1728910758653-7e990e489cac')} alt="Indian culinary" className="w-full h-56 object-cover rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-500" />
              <img src={img('rise-hero-2', 'https://images.unsplash.com/photo-1728910156510-77488f19b152')} alt="Indian cooking" className="w-full h-56 object-cover rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-500 mt-8" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Courses */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-5 justify-center mb-3">
              <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-16"></div>
              <ChefHat size={16} className="text-[#D4A574]" />
              <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-16"></div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Our Courses</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {courses.map((c, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 bg-[#FDF8F0]" data-testid={`course-${idx}`}>
                  <CardContent className="p-6 text-center">
                    <div className="bg-[#5C7A3A]/10 p-3.5 rounded-2xl inline-block mb-4 group-hover:bg-[#5C7A3A]/15 transition-colors duration-300">
                      <c.icon size={28} className="text-[#5C7A3A]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2C1810] mb-2 group-hover:text-[#5C7A3A] transition-colors duration-300">{c.title}</h3>
                    <p className="text-sm text-[#7a6555] mb-2">{c.desc}</p>
                    <p className="text-xs font-semibold text-[#C4844A]">{c.duration}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-12" style={{ background: '#FDF8F0' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>What You'll <span className="text-[#5C7A3A]">Learn</span></h2>
              <div className="space-y-4">
                {['Traditional Indian cooking techniques', 'Modern plating and presentation', 'Food safety and hygiene standards', 'Menu planning and costing', 'Kitchen management', 'Business fundamentals for food entrepreneurs'].map((item, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08, duration: 0.4 }} className="flex items-start space-x-3 group">
                    <CheckCircle size={18} className="text-[#5C7A3A] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                    <p className="text-base text-[#2C1810]">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              <img src={img('rise-learn', 'https://images.unsplash.com/photo-1728910758653-7e990e489cac')} alt="Learning" className="w-full h-80 object-cover rounded-2xl shadow-xl shadow-[#5C7A3A]/10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Franchise */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Franchise Opportunities</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-base text-[#7a6555] max-w-lg mx-auto">Bring Rise & Rolls culinary academy to your city with complete setup and support.</motion.p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {franchise.map((f, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 text-center bg-[#FDF8F0]" data-testid={`franchise-${idx}`}>
                  <CardContent className="p-6">
                    <div className="bg-[#5C7A3A]/10 p-3.5 rounded-2xl inline-block mb-4 group-hover:bg-[#5C7A3A]/15 transition-colors duration-300">
                      <f.icon size={28} className="text-[#5C7A3A]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2C1810] mb-2 group-hover:text-[#5C7A3A] transition-colors duration-300">{f.title}</h3>
                    <p className="text-sm text-[#7a6555]">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C4A1A] via-[#3B5A2B] to-[#2A3F1E]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Start Your <span className="text-[#D4A574]">Culinary Journey</span></h2>
            <p className="text-base text-white/75 mb-8 max-w-md mx-auto">Enroll in our courses or become a Rise & Rolls franchise partner</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Enroll in Courses', '_blank')} className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="rise-enroll-btn">Enroll Now</Button>
              <Button size="lg" variant="outline" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Franchise Inquiry', '_blank')} className="border-2 border-white text-white hover:bg-white hover:text-[#2C1810] font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="rise-franchise-btn">Franchise Inquiry</Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RiseAndRolls;
